import React from "react";
import { Controller } from "react-hook-form";
import { Input, Spacer, VDBText } from "@components/atoms";
import styled, { useTheme } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import RadioButton from "@components/atoms/RadioButton";
import { BuyFormProps } from "../types";
import { Keyboard } from "react-native";
import { DefaultTheme } from "styled-components/native";

const BuyForm: React.FC<BuyFormProps> = ({
  buyForm,
  t,
  itemType,
  userInfo,
  reCalculatePrices,
  selectedInvoiceCod,
  setSelectedInvoiceCod,
  onModify,
  selectedOption,
  sendValues,
  ppcValidation,
  tpValidation,
  disableRequestButton,
  toFixed,
  isBulk,
  isItemMelee = false,
  testIDPrefix = "bmh.buyForm",
}) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const onBlurPricePerCarat = (onBlur: any, value: any) => {
    toFixed("ctOffer");
    if (!value) {
      buyForm.setValue("ctOffer", "0");
    }
    onBlur();
  };

  const onBlurTotalPrice = (onBlur: any, value: any) => {
    if (userInfo?.roundUpPrices() && !isItemMelee) {
      toFixed("priceOffer");
    }
    if (!value) {
      buyForm.setValue("priceOffer", "0.00");
    }
    if (value.endsWith(".")) {
      value = value + "00";
      buyForm.setValue("priceOffer", value);
      sendValues();
    }
    onBlur();
  };

  const onChangeText = (val: any, onChange: any) => {
    const lastChar = val.slice(-1);
    const isDecimalAttempt = lastChar === ",";
    let formattedValue = val.replace(/,/g, "");

    formattedValue = formattedValue.replace(/[^0-9.]/g, "");

    const firstDecimalIndex = formattedValue.indexOf(".");
    if (firstDecimalIndex !== -1) {
      formattedValue =
        formattedValue.slice(0, firstDecimalIndex + 1) +
        formattedValue.slice(firstDecimalIndex + 1).replace(/\./g, "");
    }

    if (formattedValue[0] === ".") {
      formattedValue = "0" + formattedValue;
    }

    if (firstDecimalIndex > -1) {
      formattedValue = formattedValue.slice(0, firstDecimalIndex + 3);
    }

    if (isDecimalAttempt && !formattedValue.includes(".")) {
      formattedValue = formattedValue + ".";
    }

    onChange(formattedValue);
    reCalculatePrices("tp", formattedValue);
  };

  return (
    <>
      {!isBulk && (
        <>
          <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
            {t("bmh.buy.screenText.text")}
          </VDBText>
          <Spacer size={SpaceType.Double} />
          <RadioButtonContainer>
            <ViewContainer
              selected={selectedInvoiceCod === "COD"}
              itemType={itemType}
              onModify={onModify}
            >
              <RadioButton
                testID={`${testIDPrefix}.radio.cod`}
                label={t("bmh.buy.radio.cod.text")}
                checked={selectedInvoiceCod === "COD"}
                onPress={() => setSelectedInvoiceCod("COD")}
                labelColor={
                  selectedInvoiceCod === "COD"
                    ? theme["core-05"]
                    : theme["core-06"]
                }
                styles={
                  selectedInvoiceCod === "COD"
                    ? typography["Body-M"]
                    : typography["Body-R"]
                }
              />
            </ViewContainer>
            <Spacer size={SpaceType.Basic} />
            <ViewContainer
              selected={selectedInvoiceCod === "Invoice"}
              itemType={itemType}
              onModify={onModify}
            >
              <RadioButton
                testID={`${testIDPrefix}.radio.invoice`}
                label={t("bmh.buy.radio.invoice.text")}
                checked={selectedInvoiceCod === "Invoice"}
                onPress={() => setSelectedInvoiceCod("Invoice")}
                labelColor={
                  selectedInvoiceCod === "Invoice"
                    ? theme["core-05"]
                    : theme["core-06"]
                }
                styles={
                  selectedInvoiceCod === "Invoice"
                    ? typography["Body-M"]
                    : typography["Body-R"]
                }
              />
            </ViewContainer>
          </RadioButtonContainer>
        </>
      )}
      <Spacer size={SpaceType.large} />
      {selectedInvoiceCod === "Invoice" && (
        <>
          <Controller
            control={buyForm.control}
            render={({
              field: { onChange, onBlur, value },
              formState: { errors },
            }) => (
              <Input
                testID={`${testIDPrefix}.input.payWithIn`}
                key={selectedOption}
                inputLabel={t("bmh.input.payWithin.placeHolder.text")}
                placeholder={t("bmh.input.payWithin.placeHolder.text")}
                hideIconContainer={false}
                onBlur={onBlur}
                onChangeText={(val) => {
                  onChange(val);
                  sendValues();
                }}
                value={value === undefined ? "" : `${value}`}
                secureTextEntry={false}
                error={(errors?.payWithIn?.message ?? "") as string}
                keyboardType="numeric"
              />
            )}
            name="payWithIn"
          />
          <Spacer size={SpaceType.Big} />
        </>
      )}
      {["COD", "Invoice"].includes(selectedInvoiceCod) && (
        <>
          {itemType !== "jewelry" && (
            <>
              <Controller
                control={buyForm.control}
                render={({
                  field: { onChange, onBlur, value },
                  formState: { errors },
                }) => (
                  <Input
                    testID={`${testIDPrefix}.input.ctOffer`}
                    inputLabel={t("bmh.input.p/ct.placeHolder.text")}
                    placeholder={t("bmh.input.p/ct.placeHolder.text")}
                    leftIcon={undefined}
                    hideIconContainer={true}
                    currency={true}
                    onBlur={() => onBlurPricePerCarat(onBlur, value)}
                    onChangeText={(val) => {
                      const formattedValue = val.replace(/[^0-9]/g, "");
                      onChange(formattedValue);
                      reCalculatePrices("ppc", formattedValue);
                    }}
                    value={value}
                    error={
                      (errors?.ctOffer?.message as string) ||
                      (ppcValidation as string)
                    }
                    keyboardType="numeric"
                  />
                )}
                name="ctOffer"
              />
              <Spacer size={SpaceType.Big} />
            </>
          )}
          <Controller
            control={buyForm.control}
            render={({
              field: { onChange, onBlur, value },
              formState: { errors },
            }) => (
              <Input
                testID={`${testIDPrefix}.input.priceOffer`}
                inputLabel={t("bmh.input.totalPrice.placeHolder.text")}
                placeholder={t("bmh.input.totalPrice.placeHolder.text")}
                leftIcon={undefined}
                currency={true}
                hideIconContainer={true}
                onBlur={() => onBlurTotalPrice(onBlur, value)}
                onChangeText={(val) => onChangeText(val, onChange)}
                value={value}
                error={
                  (errors?.priceOffer?.message as string) ||
                  (tpValidation as string)
                }
                keyboardType="numeric"
              />
            )}
            name="priceOffer"
          />
          <Spacer size={SpaceType.Big} />
          <Controller
            control={buyForm.control}
            render={({
              field: { onChange, onBlur, value },
              formState: { errors },
            }) => (
              <Input
                testID={`${testIDPrefix}.input.buyComments`}
                inputLabel={t("bmh.input.comments.placeHolder.text")}
                placeholder={t("bmh.input.comments.placeHolder.text")}
                leftIcon={undefined}
                hideIconContainer={true}
                onBlur={onBlur}
                onChangeText={(val) => {
                  onChange(val);
                  if (disableRequestButton === false) {
                    sendValues();
                  }
                }}
                value={value}
                error={(errors?.buyComments?.message ?? "") as string}
                multiline={true}
                {...(!theme.isLargeScreen && {
                  blurOnSubmit: true,
                  onSubmitEditing: () => {
                    Keyboard.dismiss();
                  },
                })}
                keyboardType="default"
                returnKeyType="done"
              />
            )}
            name="buyComments"
          />
        </>
      )}
    </>
  );
};

const RadioButtonContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
const ViewContainer = styled.View<{
  selected: boolean;
  itemType?: any;
  onModify: boolean;
}>`
  flex: 1;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-02"] as string};
  border-color: ${({
    theme,
    selected,
    itemType,
  }: {
    theme: DefaultTheme;
    selected: boolean;
    itemType: any;
  }) =>
    selected
      ? itemType === "diamond"
        ? theme["diam-01"]
        : itemType === "gemstone"
        ? theme["gem-01"]
        : itemType === "jewelry"
        ? theme["jewelry-01"]
        : theme["lab-01"]
      : theme["core-02"]};
  border-width: 1px;
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  border-radius: ${SpaceType.Basic}px;
`;

export default BuyForm;
